import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, MessageSquare, Swords, Loader2, Trophy, Star, Share2 } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import UserAvatar from "@/components/shared/UserAvatar";
import { useTranslation } from "react-i18next";
import { getLevelInfo } from "@/lib/levelUtils";
import { useAvatarGradient } from "@/hooks/useAvatarGradient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import type { Tables } from "@/integrations/supabase/types";

type ConnectionRow = Tables<"player_connections">;
type MatchResultRow = Tables<"match_results">;

interface ProfileSummary {
  id: string;
  full_name: string | null;
  level: number | null;
}

const handLabels: Record<string, string> = { right: "Höger", left: "Vänster", both: "Båda" };
const typeLabels: Record<string, string> = { offensive: "Offensiv", defensive: "Defensiv", allround: "Allround" };

export default function PlayerProfile() {
  const { t } = useTranslation("common");
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: player, isLoading } = useQuery({
    queryKey: ["player-profile", id],
    queryFn: async () => {
      // PRE-EXISTING bucket-C SELECT * on player-profile detail page.
      // Reads ADMIN-only columns (phone, birth_date, etc.) but page should
      // only render bucket-B SAFE columns. After Sprint 1 Phase 2, this query
      // returns 0 rows for non-admin viewers (cross-user PII closure works).
      // FOLLOW-UP: split into (a) SAFE columns via public_profiles view +
      // (b) own-row direct + (c) admin RPC for the few admin-PII cases.
      // Until split, keep on profiles + own-row + admin RLS handles it.
      // Lesson reference: tasks/lessons.md "Forbid .from('profiles').select('*')".
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: connection } = useQuery({
    queryKey: ["player-connection", user?.id, id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_connections")
        .select("*")
        .or(`and(user_id.eq.${user!.id},friend_id.eq.${id}),and(user_id.eq.${id},friend_id.eq.${user!.id})`)
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id && !!id && user?.id !== id,
  });

  // Stats
  const { data: stats } = useQuery({
    queryKey: ["player-stats", id, organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_stats_view")
        .select("*")
        .eq("player_id", id!)
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!id && !!organization?.id,
  });

  // Player reviews (social ratings from other players)
  const { data: reviewStats } = useQuery({
    queryKey: ["player-reviews", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_match_reviews" as any)
        .select("skill_rating, sportsmanship_rating, play_again_rating, comment, created_at")
        .eq("reviewed_id", id!);
      if (!data || data.length === 0) return null;
      const count = data.length;
      const avgSkill = data.reduce((s: number, r: any) => s + r.skill_rating, 0) / count;
      const avgSport = data.reduce((s: number, r: any) => s + r.sportsmanship_rating, 0) / count;
      const avgPlay = data.reduce((s: number, r: any) => s + r.play_again_rating, 0) / count;
      const recentComments = data
        .filter((r: any) => r.comment)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 3)
        .map((r: any) => r.comment);
      return { count, avgSkill, avgSport, avgPlay, recentComments };
    },
    enabled: !!id,
  });

  // Recent matches
  const { data: recentMatches = [] } = useQuery({
    queryKey: ["player-recent-matches", id, organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("match_results")
        .select("*")
        .eq("organization_id", organization!.id)
        .or(`winner_team.cs.{${id!}},loser_team.cs.{${id!}}`)
        .order("played_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
    enabled: !!id && !!organization?.id,
  });

  // Mutual friends
  const { data: mutualFriends = [] } = useQuery({
    queryKey: ["mutual-friends", user?.id, id],
    queryFn: async () => {
      // Get my friends
      const { data: myConns } = await supabase
        .from("player_connections")
        .select("user_id, friend_id")
        .eq("status", "accepted")
        .or(`user_id.eq.${user!.id},friend_id.eq.${user!.id}`);

      const myFriendIds = new Set(
        (myConns ?? []).map((c: ConnectionRow) => c.user_id === user!.id ? c.friend_id : c.user_id)
      );

      // Get their friends
      const { data: theirConns } = await supabase
        .from("player_connections")
        .select("user_id, friend_id")
        .eq("status", "accepted")
        .or(`user_id.eq.${id},friend_id.eq.${id}`);

      const theirFriendIds = new Set(
        (theirConns ?? []).map((c: ConnectionRow) => c.user_id === id ? c.friend_id : c.user_id)
      );

      // Intersection
      const mutualIds = [...myFriendIds].filter((fid) => theirFriendIds.has(fid) && fid !== id && fid !== user!.id);
      if (mutualIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level")
        .in("id", mutualIds.slice(0, 6));

      return profiles ?? [];
    },
    enabled: !!user?.id && !!id && user?.id !== id,
  });

  const addFriendMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("player_connections")
        .insert({ user_id: user!.id, friend_id: id! });
      if (error) throw error;

      // Send notification
      await supabase.from("notifications").insert({
        user_id: id!,
        organization_id: organization!.id,  // PR-B: org-scope per Phase 1 RLS
        type: "friend_request",
        title: "Ny vänförfrågan",
        body: `${user?.user_metadata?.full_name ?? "Någon"} vill bli din vän`,
        data: { from_user_id: user!.id },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["player-connection"] });
      toast.success("Vänförfrågan skickad!");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("player_connections")
        .update({ status: "accepted" })
        .eq("id", connection!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["player-connection"] });
      toast.success("Vänförfrågan accepterad!");
    },
  });

  // Hook must be called before any early returns (Rules of Hooks)
  const { gradient, isDark } = useAvatarGradient(player?.avatar_url);

  if (isLoading) return <div className="space-y-4 pb-20"><Skeleton className="h-40" /><Skeleton className="h-60" /></div>;
  if (!player) return <p className="text-center py-12 text-muted-foreground">{t("playerProfile.unknownPlayer")}</p>;

  const isMe = user?.id === id;
  const levelInfo = getLevelInfo(player.level ?? 1);
  const isFriend = connection?.status === "accepted";
  const isPending = connection?.status === "pending";
  const receivedRequest = isPending && connection?.friend_id === user?.id;
  const winRate = stats?.total_matches ? Math.round((stats.win_rate ?? 0) * 100) : null;
  const _firstName = player.full_name?.split(" ")[0] ?? "";

  const handleShare = async () => {
    const url = `${window.location.origin}/players/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${player.full_name} — Boply`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("playerProfile.linkCopied"));
      }
    } catch { /* user cancelled share sheet */ }
  };

  return (
    <div className="pb-24">
      {/* ── Hero section with gradient background ── */}
      <div className="relative -mx-4 -mt-4 lg:-mx-8 lg:-mt-6" style={{ background: gradient }}>
        {/* Top nav: back + share */}
        <div className="flex items-center justify-between px-4 pt-4 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white shadow-sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white shadow-sm"
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Avatar + name */}
        <div className="flex flex-col items-center pb-8 pt-4 gap-4">
          <div className="relative">
            <UserAvatar
              avatarUrl={player.avatar_url}
              fullName={player.full_name}
              size="hero"
              className="shadow-2xl ring-4 ring-white/30"
            />
            <div className="absolute -bottom-1 -right-1">
              <LevelBadge level={player.level ?? 1} size="lg" />
            </div>
          </div>

          <div className="text-center space-y-1">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-foreground"}`}>
              {player.full_name || t("playerProfile.unknownPlayer")}
            </h1>
            {(player as any).bio && (
              <p className={`text-sm max-w-xs mx-auto ${isDark ? "text-white/70" : "text-muted-foreground"}`}>
                {(player as any).bio}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 lg:px-0 pt-4 max-w-md mx-auto">
        {/* ── Meta pills ── */}
        <div className="flex flex-wrap gap-2 justify-center">
          <Badge variant="outline" className="gap-1 text-xs">🎾 {t("playerProfile.padel")}</Badge>
          {player.player_type && (
            <Badge variant="outline" className="text-xs">
              {typeLabels[player.player_type] ?? player.player_type}
            </Badge>
          )}
          {player.preferred_hand && (
            <Badge variant="outline" className="text-xs">
              ✋ {player.preferred_hand === "right" ? t("playerProfile.rightHanded") : player.preferred_hand === "left" ? t("playerProfile.leftHanded") : handLabels[player.preferred_hand]}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            {t(`levelInfo.${levelInfo.labelKey}`, levelInfo.label)}
          </Badge>
        </div>

        {/* ── Quick stats ── */}
        <div className="grid grid-cols-3 gap-2">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats?.total_matches ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">{t("playerProfile.matches")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{winRate != null ? `${winRate}%` : "—"}</p>
              <p className="text-[11px] text-muted-foreground">{t("playerProfile.winRate")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats?.wins ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">{t("playerProfile.wins")}</p>
            </CardContent>
          </Card>
        </div>

        {/* ── Player reviews ── */}
        {reviewStats && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500" />
                {t("playerProfile.ratings")} ({reviewStats.count})
              </p>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div>
                  <p className="text-lg font-bold">{reviewStats.avgSkill.toFixed(1)}</p>
                  <p className="text-muted-foreground">{t("playerProfile.skill")}</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{reviewStats.avgSport.toFixed(1)}</p>
                  <p className="text-muted-foreground">{t("playerProfile.sportsmanship")}</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{reviewStats.avgPlay.toFixed(1)}</p>
                  <p className="text-muted-foreground">{t("playerProfile.playAgain")}</p>
                </div>
              </div>
              {reviewStats.recentComments.length > 0 && (
                <div className="border-t pt-2 space-y-1">
                  {reviewStats.recentComments.map((c: string, i: number) => (
                    <p key={i} className="text-xs text-muted-foreground italic">"{c}"</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Action row ── */}
        {!isMe && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1 flex-1" onClick={() => navigate(`/messages/${id}`)}>
              <MessageSquare className="h-3.5 w-3.5" /> {t("playerProfile.message")}
            </Button>
            <Button variant="outline" size="sm" className="gap-1 flex-1" onClick={() => navigate(`/book`)}>
              <Swords className="h-3.5 w-3.5" /> {t("playerProfile.invite")}
            </Button>
          </div>
        )}

        {/* ── Recent matches ── */}
        {recentMatches.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5"><Trophy className="h-4 w-4" /> {t("playerProfile.recentMatches")}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {recentMatches.map((m: MatchResultRow) => {
                const won = (m.winner_team as string[]).includes(id!);
                return (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant={won ? "default" : "outline"} className="text-[10px] px-1.5">
                        {won ? "W" : "L"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(m.played_at), "d MMM", { locale: sv })}
                      </span>
                    </div>
                    <span className="text-xs font-mono">{m.score}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* ── Mutual friends ── */}
        {mutualFriends.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t("playerProfile.mutualFriends")} ({mutualFriends.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex flex-wrap gap-2">
                {mutualFriends.map((f: ProfileSummary) => (
                  <button
                    key={f.id}
                    onClick={() => navigate(`/players/${f.id}`)}
                    className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs hover:bg-muted/80 transition-colors"
                  >
                    <span className="font-medium">{f.full_name?.split(" ")[0]}</span>
                    <LevelBadge level={f.level ?? 1} size="sm" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Sticky CTA: friend request ── */}
      {!isMe && !isFriend && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {!connection && (
            <Button onClick={() => addFriendMutation.mutate()} disabled={addFriendMutation.isPending} className="w-full gap-2">
              {addFriendMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {t("playerProfile.sendFriendRequest")}
            </Button>
          )}
          {receivedRequest && (
            <Button onClick={() => acceptMutation.mutate()} disabled={acceptMutation.isPending} className="w-full gap-2">
              <UserPlus className="h-4 w-4" />
              {t("playerProfile.acceptFriendRequest")}
            </Button>
          )}
          {isPending && !receivedRequest && (
            <Button disabled variant="outline" className="w-full">{t("playerProfile.requestSent")}</Button>
          )}
        </div>
      )}
    </div>
  );
}
